import type {
  ICustomToolFormValues,
  ICustomToolParamChild,
} from "../types/custom-integration";
import {
  ECustomIntegrationBuilderStep,
  ECustomParamLocation,
  ECustomParamType,
  ECustomToolMethod,
} from "../types/custom-integration";

export const CUSTOM_INTEGRATION_QUERY_KEY = "custom-integrations";
// NOTE: Mirrors TOOL_NAME_REGEX on the server — must start/end with [a-z0-9], underscores only in the middle.
export const CUSTOM_INTEGRATION_TOOL_NAME_REGEX =
  /^[a-z][a-z0-9_]{0,253}[a-z0-9]$|^[a-z]$/;

// NOTE: Parameter names start with an identifier-like token — a letter or
// underscore, then letters/digits/underscores/dots/hyphens — optionally
// followed by bracket groups. This rejects whitespace-only and space-containing
// names (NSAI-3557) while staying permissive enough for real OpenAPI-imported
// param names, which are populated without manual entry. Names are
// case-insensitive here because path/query/header params in the wild are not
// restricted to lowercase.
//
// NSAI-5859 widened the allowed punctuation to exactly two more things, both
// of which real APIs require and neither of which is a general escape hatch:
//   - "$" for OData system query options — "$top", "$select", "$filter".
//   - bracket groups for the deep-object style JSON:API and Rails/qs-shaped
//     APIs use, which OpenAPI specs declare verbatim — "page[number]",
//     "filter[has_images]", "filter[user][name]", "tags[]".
// Everything else stays out: the full allowed set is letters, digits, "_", ".",
// "-", "$" and balanced brackets. Neither addition needs escaping on the wire —
// encodeURIComponent turns them into %24/%5B/%5D, which these APIs decode back —
// and both survive the downstream Anthropic tool-key mapping, which replaces any
// character outside [a-zA-Z0-9_.-] (see ANTHROPIC_PARAM_KEY_UNSAFE_CHARS_REGEX
// on the server) while the original name is what goes on the request.
export const CUSTOM_INTEGRATION_PARAM_NAME_REGEX =
  /^[A-Za-z_$][\w.$-]*(?:\[[\w.$-]*\])*$/;

// NOTE: Matches a {param} placeholder inside a URL path template (e.g. /users/{id}/posts/{postId}).
// Capture group 1 is the raw param name without braces.
export const PATH_PLACEHOLDER_REGEX = /\{([^}]+)\}/g; // NOSONAR

export const EMPTY_CUSTOM_TOOL: ICustomToolFormValues = {
  toolName: "",
  displayName: "",
  description: "",
  method: ECustomToolMethod.GET,
  pathTemplate: "/",
  inputParams: [],
};

export const EMPTY_CUSTOM_PARAM_CHILD: ICustomToolParamChild = {
  name: "",
  type: ECustomParamType.STRING,
  required: false,
  description: "",
};

// NOTE: Maximum nesting depth for tool input parameters. Root params count as
// depth 1; the UI and backend both enforce this ceiling.
export const MAX_PARAM_DEPTH = 3;

// NOTE: Maximum tools that may be SAVED on one custom integration. Mirrors
// MAX_TOOLS_PER_INTEGRATION on the server, which enforces it on both create and
// update (NSAI-6059) — this copy exists so the builder can refuse before the
// request rather than surfacing a 400.
//
// Deliberately NOT the same number as the server's OpenAPI import ceiling: the
// importer intentionally offers more endpoints than can be stored, because the
// builder lists every imported endpoint as a removable row and picking a subset
// is the normal flow. This is the ceiling on what survives that pick.
export const MAX_TOOLS_PER_INTEGRATION = 30;

// NOTE: NSAI-6084 — the longest pasted OpenAPI spec (`specJson`), in characters
// (`String.length`), the server accepts. Mirrors OPENAPI_SPEC_JSON_MAX_LENGTH on
// the server, whose DTO refuses anything longer before it is parsed; this copy
// lets the web panel and the mobile screen refuse an oversized paste next to the
// field instead of sending megabytes only to get a 400. The inline message
// (`openapi.specJsonTooLong`) interpolates it through
// formatOpenApiSpecJsonMaxLength, so no translation repeats the number.
export const OPENAPI_SPEC_JSON_MAX_LENGTH = 10_000_000;

// NOTE: The builder's steps and the i18n key of each step's label, in display
// order. Consumed by both the web modal and the mobile wizard so the two stay
// in step with each other.
export const CUSTOM_INTEGRATION_BUILDER_STEPS = [
  {
    step: ECustomIntegrationBuilderStep.CONNECTION,
    labelKey: "builder.step1",
  },
  { step: ECustomIntegrationBuilderStep.TOOLS, labelKey: "builder.step3" },
  { step: ECustomIntegrationBuilderStep.REVIEW, labelKey: "builder.step4" },
] as const;

// NOTE: NSAI-6014 — these three maps hold i18n *keys*, never display text, so
// every client resolves a parameter's type and location through `t()` against
// the shared `customIntegration` bundle. Hardcoding the English here is what
// left RU/AR users reading "yes/no" in the parameter editor.
// `as const satisfies` (rather than a `Record<..., string>` annotation) keeps
// the literal key types that i18next's `strictKeyChecks` requires, while still
// failing the build if an enum member is ever left out.
export const CUSTOM_PARAM_TYPE_LABEL_KEYS = {
  [ECustomParamType.STRING]: "tools.param.typeString",
  [ECustomParamType.NUMBER]: "tools.param.typeNumber",
  [ECustomParamType.BOOLEAN]: "tools.param.typeBoolean",
  [ECustomParamType.ARRAY]: "tools.param.typeArray",
  [ECustomParamType.OBJECT]: "tools.param.typeObject",
} as const satisfies Record<ECustomParamType, string>;

// NOTE: The type dropdown in the parameter editor shows the label plus a short
// explainer ("Text — plain text"). Each option's full text is one translation
// key rather than a translated label glued to a translated suffix: word order
// and the dash differ per language, and Arabic reads right-to-left, so only the
// whole sentence can be written naturally (NSAI-6015). The bare labels above
// stay for every place that shows a type without an explainer.
export const CUSTOM_PARAM_TYPE_OPTION_LABEL_KEYS = {
  [ECustomParamType.STRING]: "tools.param.typeStringOption",
  [ECustomParamType.NUMBER]: "tools.param.typeNumberOption",
  [ECustomParamType.BOOLEAN]: "tools.param.typeBooleanOption",
  [ECustomParamType.ARRAY]: "tools.param.typeArrayOption",
  [ECustomParamType.OBJECT]: "tools.param.typeObjectOption",
} as const satisfies Record<ECustomParamType, string>;

export const CUSTOM_PARAM_LOCATION_LABEL_KEYS = {
  [ECustomParamLocation.PATH]: "tools.param.locationPath",
  [ECustomParamLocation.QUERY]: "tools.param.locationQuery",
  [ECustomParamLocation.BODY]: "tools.param.locationBody",
} as const satisfies Record<ECustomParamLocation, string>;

export const CUSTOM_PARAM_LOCATION_HINT_KEYS = {
  [ECustomParamLocation.PATH]: "tools.param.locationPathHint",
  [ECustomParamLocation.QUERY]: "tools.param.locationQueryHint",
  [ECustomParamLocation.BODY]: "tools.param.locationBodyHint",
} as const satisfies Record<ECustomParamLocation, string>;
